// Every message, on both gateway channels, and what happened to it.
//
// The rows that matter most are the ones that were never delivered: a
// 'simulated' row carries the reason in the same column a failure does,
// because "why didn't the patient get it?" is the question this screen exists
// to answer and "the switch was off" is a valid answer to it.
import { useMemo, useState } from 'react';
import { ScrollText } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { TabBar } from '@/components/ui/TabBar';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useDeliveryLog, useTemplates } from './hooks';
import { useWhatsAppTemplates } from '../whatsapp-gateway/hooks';
import type { DeliveryLogRow } from './types';

const dateTime = (v: string) =>
  new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function StatusBadge({ row }: { row: DeliveryLogRow }) {
  if (row.status === 'sent') return <Badge variant="success">Delivered</Badge>;
  if (row.status === 'failed') return <Badge variant="danger">Failed</Badge>;
  // Withheld by the patient-level policy (e.g. the patient is recorded as
  // deceased) — deliberate, not a configuration gap, so not styled as a warning.
  if (row.status === 'suppressed') return <Badge variant="neutral">Suppressed</Badge>;
  return <Badge variant="warn">Not sent</Badge>;
}

/**
 * The log table itself. SMS and WhatsApp rows share notification_log, so one
 * table renders both; the Segments column only means something for SMS.
 */
function DeliveryLogTable({
  rows,
  showSegments = true,
  templateHeading = 'Wording',
  defaultChannel = 'sms',
}: {
  rows: DeliveryLogRow[];
  showSegments?: boolean;
  templateHeading?: string;
  defaultChannel?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>To</TableHead>
            <TableHead>{templateHeading}</TableHead>
            <TableHead>Outcome</TableHead>
            {showSegments && <TableHead className="text-right">Segments</TableHead>}
            <TableHead>What happened</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="whitespace-nowrap text-[12px]">{dateTime(r.created_at)}</TableCell>
              <TableCell className="text-[12px]">
                <div className="font-mono">{r.recipient ?? '—'}</div>
                {r.patient_name && (
                  <div className="text-[11px] text-ink-muted">{r.patient_name} · {r.patient_uhid}</div>
                )}
                {!r.patient_id && r.recipient && (
                  <div className="text-[11px] text-ink-muted">Test send{r.sent_by ? ` by ${r.sent_by}` : ''}</div>
                )}
              </TableCell>
              <TableCell className="text-[12px]">
                <code className="text-[11px]">{r.template_key}</code>
                {r.channel !== defaultChannel && <div className="text-[11px] text-ink-muted">{r.channel}</div>}
                {r.message_text && (
                  <div className="mt-0.5 max-w-md truncate font-mono text-[11px] text-ink-muted">
                    {r.message_text}
                  </div>
                )}
              </TableCell>
              <TableCell><StatusBadge row={r} /></TableCell>
              {showSegments && (
                <TableCell className="text-right text-[12px]">
                  {r.segments ?? '—'}
                  {r.encoding === 'UCS2' && <div className="text-[11px] text-warn-ink">special chars</div>}
                </TableCell>
              )}
              <TableCell className="max-w-sm text-[11px] text-ink-muted">
                {r.failure_reason ?? (r.provider_message_id ? `MSG91 reference ${r.provider_message_id}` : '—')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// The three views of the one log. 'all' means both gateway channels — the
// in-app status-page rows are a different delivery and stay out of this screen.
const CHANNELS = [
  { id: 'all', label: 'All', query: 'sms,whatsapp' },
  { id: 'sms', label: 'SMS only', query: 'sms' },
  { id: 'whatsapp', label: 'WhatsApp only', query: 'whatsapp' },
] as const;

type ChannelId = (typeof CHANNELS)[number]['id'];

export default function DeliveryLogTab({ canSms = true, canWhatsApp = true }: { canSms?: boolean; canWhatsApp?: boolean }) {
  // A viewer granted only one channel is shown only that channel.
  const options = CHANNELS.filter((c) => (c.id === 'all' ? canSms && canWhatsApp : c.id === 'sms' ? canSms : canWhatsApp));
  const [status, setStatus] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [channel, setChannel] = useState<ChannelId>(options[0]?.id ?? 'sms');
  const [q, setQ] = useState('');

  const showSms = channel !== 'whatsapp';
  const showWhatsApp = channel !== 'sms';
  const smsTemplates = useTemplates({ enabled: canSms && showSms });
  const waTemplates = useWhatsAppTemplates({ enabled: canWhatsApp && showWhatsApp });

  // Both channels use the same event keys, so the filter lists each key once.
  const templateOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    if (showSms) (smsTemplates.data ?? []).forEach((t) => byKey.set(t.template_key, t.name));
    if (showWhatsApp) (waTemplates.data ?? []).forEach((t) => { if (!byKey.has(t.template_key)) byKey.set(t.template_key, t.name); });
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [smsTemplates.data, waTemplates.data, showSms, showWhatsApp]);

  const log = useDeliveryLog(
    {
      status: status || undefined,
      template_key: templateKey || undefined,
      channel: CHANNELS.find((c) => c.id === channel)?.query,
      q: q.trim() || undefined,
      limit: 200,
    },
    { via: canSms ? 'sms' : 'whatsapp' }
  );

  const rows = log.data ?? [];

  return (
    <div>
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        Every SMS and WhatsApp message this hospital has sent — and every one it deliberately did not. A row
        marked <strong>Not sent</strong> was built and checked but held back by a switch or an unregistered
        template; <strong>Suppressed</strong> means the patient may not be messaged on that channel (no WhatsApp
        opt-in, or recorded as deceased). The reason is on every row.
      </div>

      <Card>
        <CardHeader>
          <CardTitle><ScrollText size={15} /> Delivery log</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {options.length > 1 && (
              <TabBar tabs={options} active={channel} onChange={(id) => { setChannel(id); setTemplateKey(''); }} size="sm" />
            )}
            <Input
              className="h-8 w-44 text-[12px]"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Number or patient"
            />
            <NativeSelect className="h-8 w-auto text-[12px]" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any outcome</option>
              <option value="sent">Delivered</option>
              <option value="simulated">Not sent</option>
              <option value="failed">Failed</option>
              <option value="suppressed">Suppressed</option>
            </NativeSelect>
            <NativeSelect className="h-8 w-auto text-[12px]" value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
              <option value="">All templates</option>
              {templateOptions.map(([key, name]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </NativeSelect>
          </div>
        </CardHeader>

        {log.isError ? (
          <ErrorState
            message={log.error instanceof ApiError ? log.error.message : 'Could not load the delivery log'}
            onRetry={() => log.refetch()}
          />
        ) : log.isLoading ? (
          <LoadingState label="Loading delivery log…" />
        ) : rows.length === 0 ? (
          <EmptyState
            label="Nothing matches this filter"
            hint={status || templateKey || q ? 'Try clearing a filter.' : 'No messages have been sent yet.'}
          />
        ) : (
          <DeliveryLogTable
            rows={rows}
            showSegments={showSms}
            templateHeading="Template"
            // In "All" every row names its channel; in a single-channel view it would only repeat itself.
            defaultChannel={channel === 'all' ? '' : channel}
          />
        )}
      </Card>
    </div>
  );
}
